// Settings panel — Connect / disconnect Google Calendar.
//
// Flow:
//   - Not connected → "Connect Google Calendar" button → opens
//     /api/google-calendar/oauth-start in a new tab (gets a 302 to
//     Google's consent screen).
//   - After consent, Google redirects back to /api/oauth-callback,
//     which redirects to /?google=connected. The query-param watcher
//     here refreshes status + fires the "sync existing appts" button.
//   - Connected → show email + sync toggle + Disconnect + Sync-now.

import React from 'react';
import { readPersistedJwtDirect } from '../lib/authedFetch';
import { Calendar, CheckCircle2, X, RefreshCcw, AlertTriangle } from 'lucide-react';
import { supabase } from '../lib/supabase';

async function apiFetch(path, opts = {}) {
  const jwt = readPersistedJwtDirect();
  const res = await fetch(path, {
    ...opts,
    headers: { 'content-type': 'application/json', ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}), ...(opts.headers || {}) },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body?.error || `HTTP ${res.status}`);
  return body;
}

export default function GoogleCalendarPanel({ currentUser, showToast = () => {} }) {
  const [status, setStatus] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [busy, setBusy]     = React.useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    try { setStatus(await apiFetch('/api/google-calendar/status')); }
    catch (err) { showToast(`Load failed: ${err.message}`); }
    finally { setLoading(false); }
  }, [showToast]);

  React.useEffect(() => { load(); }, [load]);

  // Watch for ?google= query param so the panel auto-refreshes after
  // the OAuth round-trip lands the user back on the CRM.
  React.useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const g = q.get('google');
    if (!g) return;
    if (g === 'connected') {
      const email = q.get('email');
      showToast(`Google Calendar connected${email ? ` (${email})` : ''}.`);
      load();
    } else if (g === 'denied')           showToast('Google Calendar connection denied.');
    else if (g === 'no_refresh_token')   showToast('Google didn\'t return a refresh token — revoke access in your Google account, then try again.');
    else if (g === 'failed')             showToast(`Connection failed: ${q.get('error') || 'unknown'}`);
    // Clean the URL so reloading doesn't re-fire the toast.
    q.delete('google'); q.delete('email'); q.delete('error');
    const newSearch = q.toString();
    window.history.replaceState({}, '', window.location.pathname + (newSearch ? '?' + newSearch : ''));
  }, [load, showToast]);

  const connect = async () => {
    setBusy(true);
    try {
      const { url } = await apiFetch('/api/google-calendar/oauth-start?json=1');
      window.location.href = url;
    } catch (err) {
      showToast('Connect failed: ' + err.message);
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm('Disconnect Google Calendar? Existing synced events stay on Google but new appointments will stop syncing.')) return;
    setBusy(true);
    try { await apiFetch('/api/google-calendar/status', { method: 'DELETE' }); showToast('Disconnected.'); await load(); }
    catch (err) { showToast(`Disconnect failed: ${err.message}`); }
    finally { setBusy(false); }
  };

  const toggle = async (enabled) => {
    setBusy(true);
    try { await apiFetch('/api/google-calendar/status', { method: 'POST', body: JSON.stringify({ sync_enabled: enabled }) }); await load(); }
    catch (err) { showToast(`Toggle failed: ${err.message}`); }
    finally { setBusy(false); }
  };

  const syncNow = async () => {
    setBusy(true);
    try {
      const r = await apiFetch('/api/google-calendar/sync-now', { method: 'POST' });
      showToast(`Synced ${r.synced} of ${r.eligible} appointments${r.failed > 0 ? ` (${r.failed} failed)` : ''}.`);
      await load();
    } catch (err) { showToast(`Sync failed: ${err.message}`); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="bg-white rounded-xl border border-slate-200 p-5 text-sm text-slate-500">Loading Google Calendar status…</div>;
  if (!status?.configured) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex items-center gap-2 mb-2"><Calendar size={16} className="text-amber-600"/><h3 className="font-bold text-slate-900">Google Calendar</h3></div>
        <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-3">
          Not configured on the server yet. Set <code>GOOGLE_OAUTH_CLIENT_ID</code> + <code>GOOGLE_OAUTH_CLIENT_SECRET</code> on Vercel (and add this CRM URL as an authorized redirect in your Google Cloud OAuth client) to enable connecting.
        </div>
      </div>
    );
  }

  if (!status.connected) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex items-center gap-2 mb-1"><Calendar size={16} className="text-amber-600"/><h3 className="font-bold text-slate-900">Google Calendar</h3></div>
        <p className="text-xs text-slate-500 mb-3">Connect your Google Calendar so every CRM appointment lands on your phone with Google's native push notifications. Two-tap to set up.</p>
        <button onClick={connect} disabled={busy}
          className="bg-slate-900 hover:bg-black disabled:opacity-50 text-white font-bold px-5 py-2.5 rounded-md inline-flex items-center gap-2">
          <Calendar size={14}/> Connect Google Calendar
        </button>
      </div>
    );
  }

  const link = status.link;
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Calendar size={16} className="text-amber-600"/>
          <h3 className="font-bold text-slate-900">Google Calendar</h3>
          <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-emerald-50 text-emerald-700 border border-emerald-200 rounded px-1.5 py-0.5">
            <CheckCircle2 size={10}/> Connected
          </span>
        </div>
        <button onClick={load} className="text-slate-400 hover:text-slate-700"><RefreshCcw size={14}/></button>
      </div>
      <div className="text-xs text-slate-600">
        Connected as <strong>{link.google_email}</strong> · calendar: <code className="text-[11px]">{link.calendar_id}</code>
        {link.last_synced_at && <> · last sync {new Date(link.last_synced_at).toLocaleString()}</>}
      </div>
      {link.last_sync_error && (
        <div className="bg-rose-50 border border-rose-200 text-rose-800 text-xs rounded p-2 inline-flex items-start gap-1.5">
          <AlertTriangle size={12} className="mt-0.5 flex-shrink-0"/>{link.last_sync_error}
        </div>
      )}
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={link.sync_enabled} onChange={e => toggle(e.target.checked)} disabled={busy}/>
        Sync new appointments to Google
      </label>
      <div className="flex gap-2 pt-2 border-t border-slate-100">
        <button onClick={syncNow} disabled={busy} className="text-xs font-bold uppercase tracking-wider bg-slate-900 hover:bg-black text-white rounded-md px-3 py-2 disabled:opacity-50">
          Sync existing appointments
        </button>
        <button onClick={disconnect} disabled={busy} className="text-xs font-bold uppercase tracking-wider text-rose-700 hover:text-rose-900 px-3 py-2 inline-flex items-center gap-1">
          <X size={11}/> Disconnect
        </button>
      </div>
    </div>
  );
}
